// Cross-cutting API contract. Every path is relative to the API origin
// (https://app.growmyx.com in prod, http://localhost:8787 in dev) and lives
// under /api. Feature-owned endpoints (autoresponder, outreach, engage,
// comments, scheduler, analytics, overlay) are documented in
// ./features/<feature>.ts by the feature that owns them.
//
// Auth: the portal uses an HttpOnly cookie session; the extension sends
// `Authorization: Bearer <token>`. Both resolve to the same users/sessions.
// Errors: non-2xx with body ApiError { error, code? }.

import type {
  Activity,
  ActivityType,
  Contact,
  EngineStatus,
  Feature,
  ID,
  IgAccount,
  IgAccountDriver,
  IgUserLite,
  IgUserProfile,
  Me,
  Millis,
  Note,
  Page,
  PipelineStage,
  Plan,
  Role,
  SettingsMap,
  StageKind,
  Tag,
  Task,
  User,
  UserStatus,
} from "./types";
import type { Entitlements, FeatureSwitches, PlanTier, ResellerPackage } from "./plans";

// ── Auth ──────────────────────────────────────────────────────────────────
// POST /api/auth/login               LoginReq → LoginRes (web: also Set-Cookie)
// POST /api/auth/logout              → { ok }
// GET  /api/auth/me                  → { user: Me }
// POST /api/auth/forgot              { email } → { ok }   (always ok; emails a reset link)
// POST /api/auth/reset               { token, password } → { ok }   (reset + invite links)
// POST /api/auth/change-password     { current, next } → { ok }
// POST /api/auth/ext-token           (cookie) → LoginRes with token — portal hands the extension a token (SSO)
// POST /api/auth/handoff             (bearer) → { code } — one-time 60s code; extension opens <portal>/sso?code=
// POST /api/auth/handoff/redeem      { code } → { user } + Set-Cookie
// POST /api/auth/impersonate         { userId } (admin, or the user's reseller) → { user } + Set-Cookie
// POST /api/auth/stop-impersonating  → { user } + Set-Cookie (back to the impersonator)

export type AuthClient = "web" | "ext";

export interface LoginReq {
  email: string;
  password: string;
  client: AuthClient;
}

export interface LoginRes {
  user: Me;
  /** Only for client === "ext" (and /ext-token). */
  token?: string;
}

/** Message the portal posts to the extension's portal content script (SSO bridge). */
export interface SsoBridgeMessage {
  source: "growmyx-portal";
  type: "sso-token" | "sso-logout" | "sso-ping" | "scheduler-publish-now";
  token?: string;
  requestId?: string;
  postId?: string;
  igAccountId?: string;
}

export interface PublishNowResult { accepted: boolean; message: string }
export interface PublishNowReply extends PublishNowResult {
  source: "growmyx-extension";
  type: "scheduler-publish-result";
  requestId: string;
}

/** Reply the extension posts back to the portal page. */
export interface SsoBridgeReply {
  source: "growmyx-extension";
  type: "sso-ack" | "sso-pong";
  version: string;
  signedIn: boolean;
}

// ── Reseller: manage customer accounts ────────────────────────────────────
// GET    /api/reseller/accounts?q=&cursor=&limit=     → ResellerAccountsRes
// POST   /api/reseller/accounts                       CreateAccountReq → CreateAccountRes
// PATCH  /api/reseller/accounts/:id                   UpdateAccountReq → { user }
// POST   /api/reseller/accounts/:id/invite            { sendEmail?: false } → { ok, inviteLink, expiresAt, emailed }
// POST   /api/reseller/accounts/:id/reset-password    { password? } → { ok, inviteLink? }  (no password = email a reset link)
// DELETE /api/reseller/accounts/:id                   → { ok }

export interface ResellerAccount extends User {
  /** Whether a password has been set; distinct from sign-in history. */
  activation: "invited" | "ready";
}

export interface ResellerAccountsRes extends Page<ResellerAccount> {
  seatLimit: number;
  seatsUsed: number;
}

export interface CreateAccountReq {
  email: string;
  name: string;
  /** Set a password directly, or leave empty and send an invite email. */
  password?: string;
  sendInvite?: boolean;
  plan?: Plan;
  /** Admin only. */
  role?: Role;
  /** Admin only (resellers). */
  seatLimit?: number;
  /** Admin only: assign to a reseller. */
  parentId?: ID | null;
  igAccountLimit?: number;
  /** Put the account on one of the parent reseller's packages (plan = the package's tier). */
  packageId?: ID | null;
  /** Feature switches on top of the plan or package (./plans switchesBetween). */
  featureSwitches?: FeatureSwitches | null;
}

export interface CreateAccountRes {
  user: User;
  /** Set-password link (also emailed when sendInvite). Shown once so the reseller can share it. */
  inviteLink?: string;
  /** Whether the invite email actually went out (false when mail isn't configured). */
  emailed?: boolean;
}

/** POST /api/{reseller/accounts|admin/users}/:id/reset-password */
export interface ResetPasswordRes {
  ok: true;
  inviteLink?: string;
  emailed?: boolean;
}

/** PATCH /api/auth/me — update your own profile. */
export interface UpdateMeReq {
  name?: string;
}

export interface UpdateAccountReq {
  name?: string;
  status?: UserStatus;
  plan?: Plan;
  /** Admin only below. */
  role?: Role;
  seatLimit?: number;
  igAccountLimit?: number;
  parentId?: ID | null;
  /** One of the parent reseller's packages, or null to leave it (plan stays the package's tier).
   *  Setting a plain `plan` also leaves the package. */
  packageId?: ID | null;
  /** Feature switches on top of the plan or package; null clears them. Changing the plan or package
   *  without sending this clears them too (pick the plan, then customize). */
  featureSwitches?: FeatureSwitches | null;
}

// ── Plans: reseller packages ───────────────────────────────────────────────
// A package is a reseller's own named plan: a standard tier plus feature switches (./plans).
// GET    /api/reseller/packages               → { items: ResellerPackage[] }  (accountCount set)
// POST   /api/reseller/packages               PackageReq → { package: ResellerPackage }
// PATCH  /api/reseller/packages/:id           Partial<PackageReq> → { package }  (its accounts follow at once)
// DELETE /api/reseller/packages/:id           → { ok, accounts }  (its accounts stay on the package's tier)
// GET    /api/admin/users/:id/packages        → { items }  a customer: the packages it can be put on (its reseller's);
//                                               a reseller: its own (for creating a customer under it)
// A package's features are enforced like any plan: over-limit live campaigns are paused.

export interface PackageReq {
  name: string;
  description?: string;
  priceLabel?: string;
  basePlan: PlanTier;
  switches?: FeatureSwitches;
}

export interface PackagesRes {
  items: ResellerPackage[];
}

// ── Admin ─────────────────────────────────────────────────────────────────
// GET    /api/admin/users?q=&role=&parentId=&cursor=&limit=   → Page<User & { seatsUsed?: number }>
// POST   /api/admin/users                 CreateAccountReq → CreateAccountRes
// PATCH  /api/admin/users/:id             UpdateAccountReq → { user }
// POST   /api/admin/users/:id/reset-password  { password? } → { ok, inviteLink? }
// DELETE /api/admin/users/:id             → { ok }
// GET    /api/admin/stats                 → AdminStats

export interface AdminStats {
  users: number;
  resellers: number;
  activeUsers7d: number;
  igAccounts: number;
  actions24h: number;
}

// ── X accounts ────────────────────────────────────────────────────
// GET    /api/ig-accounts           → { items: IgAccount[] }   (each with its `driver` browser)
// GET    /api/ig-accounts/overview  → { items: IgAccountOverview[], limit, used }   (see IgAccountOverview)
// PUT    /api/ig-accounts/:id/driver/release → IgAccount   (frees the driver seat now; a browser still
//                                    bound to the account re-takes it on its next heartbeat)
// DELETE /api/ig-accounts/:id       → { ok }   (disconnects + deletes its data)

// ── Extension engine plumbing ─────────────────────────────────────────────
// POST /api/ext/heartbeat   HeartbeatReq → HeartbeatRes
//   Called by the extension every few minutes and whenever the logged-in IG
//   account changes. Upserts the IgAccount (403 code "ig_account_limit" when
//   the plan's limit is reached) and returns everything the engine needs.

//
// Workspaces (multiple X accounts). One extension installation (= one Chrome profile)
// drives one X account at a time; the account it drives is its "binding".
//   intent "sync"    — the account must already be connected to this user, else 404
//                      `ig_account_not_connected` (never creates one). Used by every routine
//                      heartbeat and to auto-bind a viewer that is already one of the user's accounts.
//   intent "connect" — explicit user choice in the popup: creates the account if needed (403
//                      `ig_account_limit` past the plan limit) and takes the driver seat here.
//   (no intent)      — legacy extensions (≤ 0.1.4): old upsert behaviour, no driver bookkeeping.
// Driver seat: if another installation drove this account within IG_DRIVER_STALE_MS, a "sync"
// heartbeat gets `driverConflict` (and must not run jobs) unless it sends `takeover: true`.

export interface HeartbeatReq {
  profile: IgUserProfile;
  engine: EngineStatus;
  extensionVersion: string;
  intent?: "sync" | "connect";
  /** Random id generated once per extension install (per Chrome profile). Required with `intent`. */
  installationId?: string;
  /** User-chosen name for this browser, else a generated "Chrome on Windows". ≤ 60 chars. */
  browserLabel?: string;
  /** Take the driver seat from another browser that is still active. */
  takeover?: boolean;
}

export interface HeartbeatRes {
  igAccount: IgAccount;
  /** Settings for THIS X account (per-account overrides merged over the user's defaults). */
  settings: SettingsMap;
  serverTime: Millis;
  /** Set when another browser holds the driver seat. Don't run jobs; offer "Run it here instead". */
  driverConflict?: IgAccountDriver | null;
  /** What the account's plan includes. The engine skips jobs for locked features (PLAN_JOB_FEATURE).
   *  Missing from older APIs: run everything. */
  entitlements?: Entitlements;
}

/** A driver that has not heartbeated for this long is stale; any other browser may take over. */
export const IG_DRIVER_STALE_MS = 15 * 60 * 1000;

/** Header the extension engine sends on job API calls so the API can refuse a non-driver browser. */
export const INSTALLATION_HEADER = "x-gf-installation";

// GET /api/ig-accounts/overview → { items: IgAccountOverview[], limit, used }
//   One row per connected account for the all-accounts view (Settings → X accounts, Home).
//   limit = the plan's X account limit, used = accounts connected. "Today" starts at local
//   midnight in THAT account's automation timezone. today.actions = automated X actions
//   (Analytics' ACTION_TYPES by AUTOMATION_FEATURES); dmsSent = dm_out, follows = follow (any
//   feature, manual included); replies = distinct people whose DMs came in today (dm_in; needs inbox sync).
export interface IgAccountOverview {
  account: IgAccount;
  /** Activities logged since local midnight in THIS account's automation timezone, by kind. */
  today: { actions: number; dmsSent: number; follows: number; replies: number };
  /** Contacts in this account's CRM. */
  contacts: number;
  automationEnabled: boolean;
}

// ── Harvest (audience building for outreach + engage) ─────────────────────
// Campaigns whose audience comes from X (followers of X, hashtag…)
// ask the extension to fetch users. The server decides what to fetch; the
// extension's core harvester executes it and reports back.
// POST /api/harvest/next     { igAccountId } → { tasks: HarvestTask[] }
// POST /api/harvest/report   HarvestReport → { added: number }

export type AudienceSource =
  /** Available followers observed from X; continuation can be incomplete. */
  | { type: "followers_of"; username: string }
  /** Everyone who liked or commented on the account's recent posts (warmer + far larger than followers_of). */
  | { type: "engagers_of"; username: string; posts?: number; include?: "likers" | "commenters" | "both" }
  | { type: "following_of"; username: string }
  | { type: "likers_of"; mediaUrl: string }
  | { type: "commenters_of"; mediaUrl: string }
  | { type: "hashtag"; tag: string }
  | { type: "location"; locationId: string }
  | { type: "my_followers" }
  /** X's account-specific Suggested for you list. */
  | { type: "suggested_people" }
  | { type: "my_new_followers" };

export type CampaignKind = "outreach" | "engage";

export interface HarvestTask {
  campaignKind: CampaignKind;
  campaignId: ID;
  source: AudienceSource;
  cursor: string | null;
  /** How many more users the campaign wants right now. */
  want: number;
}

export interface HarvestReport {
  igAccountId: ID;
  campaignKind: CampaignKind;
  campaignId: ID;
  users: IgUserLite[];
  nextCursor: string | null;
  exhausted: boolean;
  error?: string;
}

// ── Settings ──────────────────────────────────────────────────────────────
// GET /api/settings?igAccountId=        → SettingsMap (stored values merged over defaults)
// PUT /api/settings/:key?igAccountId=   { value } → { value }   (automation/voice validated + clamped server-side)
//   With igAccountId: that X account's settings (its own overrides over the user's rows;
//   404 if it isn't the user's account). Without it: the user-level rows, which are the template
//   every account starts from (kept for older clients — the portal always sends igAccountId).

// ── Activities ────────────────────────────────────────────────────────────
// POST /api/activities   LogActivitiesReq → { ok }
//   For actions the extension performed outside a feature endpoint (overlay
//   quick actions, manual sends). The server upserts contacts from `profile`.
// GET  /api/activities?igAccountId=&feature=&type=&cursor=&limit=  → Page<Activity>

export interface LogActivityItem {
  contactId?: ID;
  /** Create/enrich the contact from this profile when given. */
  profile?: IgUserProfile;
  type: ActivityType;
  feature: Feature;
  refId?: ID;
  summary: string;
  meta?: Record<string, unknown>;
  at?: Millis;
}

export interface LogActivitiesReq {
  igAccountId: ID;
  items: LogActivityItem[];
}

// ── Media (R2) ────────────────────────────────────────────────────────────
// POST /api/media            multipart "file" → MediaRef
// PUT  /api/media/upload     raw body = the file, Content-Length required, ?width=&height= hints → MediaRef (201)
//                             streamed to R2 without buffering: MP4 ≤ 100 MB, images ≤ 30 MB (411/413/415 on bad input)
// GET  /api/media/:key       → the file (auth: owner only)

export interface MediaRef {
  key: string;
  mime: string;
  size: number;
  width?: number;
  height?: number;
}

// ── CRM (owned by the CRM feature; contract fixed here because the
//    overlay and other features call it) ───────────────────────────────────
// GET    /api/crm/stages                         → { items: PipelineStage[] }
// POST   /api/crm/stages                         { name, color, kind } → PipelineStage
// PATCH  /api/crm/stages/:id                     Partial<{ name, color, kind }> → PipelineStage
// DELETE /api/crm/stages/:id                     ?moveTo=<stageId> → { ok }
// POST   /api/crm/stages/reorder                 { ids: ID[] } → { ok }
// GET    /api/crm/tags                           → { items: Tag[] }
// POST   /api/crm/tags                           { name, color } → Tag
// PATCH  /api/crm/tags/:id  | DELETE /api/crm/tags/:id
// GET    /api/crm/board?igAccountId=             → BoardRes
// GET    /api/crm/contacts?…ContactQuery         → Page<Contact>
// GET    /api/crm/contacts/export.csv?igAccountId=   → text/csv
// POST   /api/crm/contacts                       UpsertContactReq → Contact  (upsert by igAccountId+igUserId)
// GET    /api/crm/contacts/lookup?igAccountId=&igUserId=&username=  → ContactDetail (404 code "not_found")
// GET    /api/crm/contacts/statuses?igAccountId=&igUserIds=1,2,3  → { items: Record<igUserId, ContactBadge> }
// GET    /api/crm/contacts/:id                   → ContactDetail
// PATCH  /api/crm/contacts/:id                   UpdateContactReq → Contact
// DELETE /api/crm/contacts/:id                   → { ok }
// POST   /api/crm/contacts/bulk                  BulkContactsReq → { updated: number }
// GET    /api/crm/contacts/:id/activities?cursor=&limit=  → Page<Activity>
// POST   /api/crm/contacts/:id/notes             { body } → Note
// PATCH  /api/crm/notes/:id  { body } → Note   |  DELETE /api/crm/notes/:id
// GET    /api/crm/tasks?igAccountId=&status=open|done|all&contactId=  → { items: TaskWithContact[] }
// POST   /api/crm/tasks                          { contactId?, igAccountId?, title, dueAt? } → Task
// PATCH  /api/crm/tasks/:id                      { title?, dueAt?, done? } → Task  |  DELETE /api/crm/tasks/:id

export interface ContactQuery {
  igAccountId?: ID;
  stageId?: ID;
  tagId?: ID;
  source?: string;
  q?: string;
  sort?: "recent" | "created" | "value" | "score" | "name";
  cursor?: string;
  limit?: number;
}

export interface UpsertContactReq {
  igAccountId: ID;
  profile: IgUserProfile;
  stageId?: ID;
  tagIds?: ID[];
  source?: Contact["source"];
  dealValue?: number;
}

export interface UpdateContactReq {
  stageId?: ID | null;
  dealValue?: number;
  tagIds?: ID[];
  score?: number;
  fullName?: string;
}

export interface BulkContactsReq {
  ids: ID[];
  action: "stage" | "tag" | "untag" | "delete";
  value?: ID;
}

export interface ContactDetail {
  contact: Contact;
  notes: Note[];
  tasks: Task[];
  activities: Activity[];
}

export interface ContactBadge {
  contactId: ID;
  stageId: ID | null;
  tagIds: ID[];
}

export interface TaskWithContact extends Task {
  contact: Pick<Contact, "id" | "username" | "fullName" | "profilePicUrl"> | null;
}

export interface BoardRes {
  stages: PipelineStage[];
  /** Up to 100 most recently active contacts per stage. */
  columns: Record<ID, Contact[]>;
  totals: Record<ID, { count: number; value: number }>;
}

export type { StageKind, Millis };
